import { UserRoleConstants } from '@app/core/staff/store/staff.model';
import { EFeatureFlag, FeatureFlagValue } from '@app/feature-toggle-list';

//

export type MenuConfigItemFeatureFlagHash = {
  [K in FeatureFlagValue] ?:boolean;
};
/**
 * @example
 *  featureFlags: {
 *    any: {
 *      // EITHER flag-1 must be `true` OR flag-2 must be `false`
 *      'flag-1': true,
 *      'flag-2': false,
 *    },
 *    'flag-3': true,  // This one must be `true`
 *    'flag-4': false, // This one must be `false`
 *  },
 */
export type MenuConfigItemFeatureFlags = MenuConfigItemFeatureFlagHash & {
  any ?:MenuConfigItemFeatureFlagHash;
};
export type MenuConfigItemFeatureFlagsEntry =
  | ['any', MenuConfigItemFeatureFlagHash]
  | [FeatureFlagValue, boolean];

export type MenuConfigItem = {
  theme ?:| 'gray'
    | 'cloud'
    | 'purple'
    | 'blue'
    | 'yellow'
    | 'green'
    | 'red'
    | 'orange'; // see styles/var/_color.scss
  title :string;
  icon ?:Icons;
  page ?:string;
  translate ?:string;
  permissions ?:RPermissionList[] | string[];
  oldPermissions ?:number[] | '*';
  hideViewOnlyUsers ?:boolean;
  adminOnly ?:boolean;
  submenu ?:MenuConfigItem[];
  featureFlags ?:MenuConfigItemFeatureFlags | FeatureFlagValue[];
  linkActiveExactMatch ?:boolean;
  isHiddenByDefault ?:boolean;
  parent ?:boolean;
};

//

function menuItems({} :Record<string, any>) :MenuConfigItem[] {
  return [
    {
      theme: 'purple',
      title: 'Inventory',
      icon: 'box-regular',
      oldPermissions: '*',
      permissions: [
        'skus.read',
        'stocks.read',
        'adjustmentEntries.read',
        'purchaseOrders.read',
        'receivingReports.read',
        'defectiveReports.read',
      ],
      parent: true,
      submenu: [
        {
          title: 'SKUs',
          page: '/inventory/skus',
          oldPermissions: '*',
          permissions: ['skus.read'],
        },
        {
          title: 'Stocks',
          page: '/inventory/stocks',
          oldPermissions: '*',
          permissions: ['stocks.read'],
        },
        {
          title: 'Stocks Grid',
          page: '/inventory/grid-stocks',
          oldPermissions: '*',
          permissions: ['stocks.read'],
          featureFlags: [EFeatureFlag.STOCKS_AG_GRID_REVAMP],
        },
        {
          title: 'Purchase Orders',
          page: '/orders',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          hideViewOnlyUsers: true,
          permissions: ['purchaseOrders.read'],
        },
        {
          title: 'Transfer Orders',
          page: '/inventory/transfer-order',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          permissions: [''],
        },
        {
          title: 'Inbound Shipments',
          page: '/inventory/inbound-shipments',
          hideViewOnlyUsers: true,
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          permissions: [''],
        },
        {
          title: 'Receiving',
          page: '/inventory/receiving',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['receivingReports.read'],
        },
        {
          title: 'Defectives',
          page: '/inventory/defectives',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          permissions: ['defectiveReports.read'],
        },
        {
          title: 'Assembly Orders',
          page: '/assembly-orders',
          permissions: [''],
          featureFlags: [EFeatureFlag.ASSEMBLY_ORDER],
        },
        {
          title: 'Adjustment Entries',
          page: '/inventory/adjustment-entries',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          permissions: ['adjustmentEntries.read'],
        },
        {
          title: 'Inventory Admin',
          submenu: [
            {
              title: 'Bills of Materials',
              page: '/bom',
              oldPermissions: [
                UserRoleConstants.SUPER_ADMIN,
                UserRoleConstants.ADMIN,
                UserRoleConstants.FACTORY_ADMIN,
              ],
              permissions: [''],
              featureFlags: {
                [EFeatureFlag.ASSEMBLY_ORDER]: true,
              },
            },
            {
              title: 'Cost Layers',
              page: '/cost-layers',
              oldPermissions: '*',
              permissions: ['skuUnitCost.read'],
            },
            {
              title: 'Product Categories',
              page: '/product-categories',
              oldPermissions: '*',
              permissions: ['productCategories.read'],
            },
            {
              title: 'Units of Measure',
              page: '/units-of-measure',
              oldPermissions: '*',
              permissions: ['unitOfMeasurements.read'],
            },
          ],
        },
      ],
    },
    {
      theme: 'blue',
      title: 'Sales',
      icon: 'circle-dollar-regular',
      oldPermissions: [
        UserRoleConstants.SUPER_ADMIN,
        UserRoleConstants.ADMIN,
        UserRoleConstants.FACTORY_ADMIN,
        UserRoleConstants.SALES_MEMBER,
      ],
      permissions: [
        'companies.read',
        'companyContacts.read',
        'invoices.read',
        'estimates.read',
        'clientSkuTemplates.read',
        'pricingStructureTemplates.read',
        'clientTypes.read',
      ],
      parent: true,
      submenu: [
        {
          title: 'CRM',
          page: '/sales/crm',
          oldPermissions: '*',
          hideViewOnlyUsers: true,
          permissions: ['companies.read', 'companyContacts.read'],
        },
        {
          title: 'Invoices',
          page: '/sales/view/invoices',
          oldPermissions: '*',
          hideViewOnlyUsers: true,
          permissions: ['invoices.read'],
        },
        {
          title: 'Invoices Grid',
          page: '/sales/view/grid-invoices',
          oldPermissions: '*',
          hideViewOnlyUsers: true,
          permissions: ['invoices.read'],
        },
        {
          title: 'Pricing',
          page: '/pricing',
          oldPermissions: '*',
          permissions: ['pricing.read'],
        },
        {
          title: 'Sales Admin',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          hideViewOnlyUsers: true,
          permissions: [
            'clientSkuTemplates.read',
            'pricingStructureTemplates.read',
            'clientTypes.read',
          ],
          submenu: [
            {
              title: 'B2B Product Templates',
              page: '/sales/clients/product-template',
              oldPermissions: '*',
              permissions: [''],
            },
            {
              title: 'Client Types',
              page: '/sales/client-types',
              oldPermissions: '*',
              permissions: [''],
            },
            {
              title: 'Invoice Types',
              page: '/client-purchase-order-types',
              oldPermissions: '*',
              permissions: ['clientInvoiceOrderTypes.read'],
            },
          ],
        },
      ],
    },
    {
      theme: 'yellow',
      title: 'Shipping',
      icon: 'cart-flatbed-boxes-regular',
      oldPermissions: [
        UserRoleConstants.SUPER_ADMIN,
        UserRoleConstants.ADMIN,
        UserRoleConstants.FACTORY_ADMIN,
        UserRoleConstants.SALES_MEMBER,
      ],
      permissions: [
        'salesPurgatory.read',
        'salesOrders.read',
        'picklists.read',
        'channelShippings.read',
        'shippingMatrix.read',
      ],
      parent: true,
      submenu: [
        {
          title: 'Purgatory',
          page: '/shipping/purgatory',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['salesPurgatory.read'],
        },
        {
          title: 'Sales Orders',
          page: '/shipping/posted-orders',
          oldPermissions: '*',
          permissions: ['salesOrders.read'],
        },
        {
          title: 'Sales Order Grid',
          page: '/shipping/grid-posted-orders',
          oldPermissions: '*',
          permissions: ['salesOrders.read'],
        },
        {
          title: 'Picklists',
          page: '/shipping/picklists',
          oldPermissions: '*',
          permissions: ['picklists.read'],
        },
        {
          title: 'Shipping Admin',
          oldPermissions: '*',
          permissions: [''],
          featureFlags: {
            'red_DV-3362_fo_shipment_splits': true,
          },
          submenu: [
            {
              title: 'Fulfillment Priority Lists',
              page: '/shipping/admin/fulfillment-priority-list',
              oldPermissions: '*',
              permissions: [''],
            },
            {
              title: 'Smart Fulfillment Settings',
              page: '/shipping/admin/smart-fulfillment-automation-settings',
              oldPermissions: '*',
              permissions: [''],
            },
          ],
        },
        {
          title: 'Channel Shippings',
          page: '/inventory/channel-shipping',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          permissions: ['channelShippings.read'],
          isHiddenByDefault: true,
          featureFlags: {
            'utah_UTAH-120_remove_channel_shippings': false,
          },
        },
        {
          title: 'Shipping Matrix',
          page: '/shipping-matrix',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          permissions: ['shippingMatrix.read'],
          isHiddenByDefault: true,
        },
      ],
    },
    {
      theme: 'green',
      title: 'Manage',
      icon: 'square-sliders-vertical-regular',
      oldPermissions: '*',
      permissions: [
        'suppliers.read',
        'staff.read',
        'locations.read',
        'channels.read',
        'pipelines.read',
        'productCategories.read',
        'tags.read',
        'variantTypes.read',
        'clientInvoiceOrderTypes.read',
        'unitOfMeasurements.read',
        'roles.read',
        'customFields.read',
        'templates.read',
      ],
      parent: true,
      submenu: [
        {
          title: 'Suppliers',
          page: '/factories/list',
          translate: 'MENU.FACTORIES',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          permissions: ['suppliers.read'],
        },
        {
          title: 'Staff',
          page: '/staff',
          translate: 'MENU.STAFF',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          permissions: ['staff.read'],
        },
        {
          title: 'Locations',
          page: '/locations',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          permissions: ['locations.read'],
        },
        {
          title: 'Channels',
          page: '/channels/v2/list',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          permissions: ['channels.read'],
          linkActiveExactMatch: true,
        },
        {
          title: 'Channel Product Mapping',
          page: '/channels/v2/product-mapping',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
            UserRoleConstants.STAFF,
          ],
          permissions: ['channels.read'],
          linkActiveExactMatch: true,
        },
        {
          title: 'Pipelines',
          page: '/pipelines',
          oldPermissions: '*',
          permissions: ['pipelines.read'],
          isHiddenByDefault: true,
        },
        {
          title: 'Tags',
          page: '/product-tags',
          oldPermissions: '*',
          permissions: ['tags.read'],
        },
        {
          title: 'Variant Types',
          page: '/variant-types',
          oldPermissions: '*',
          permissions: ['variantTypes.read'],
          isHiddenByDefault: true,
        },
        {
          title: 'Custom Fields',
          page: '/custom-fields',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['customFields.read'],
          featureFlags: [EFeatureFlag.CUSTOM_FIELDS],
        },
        {
          title: 'Templates',
          page: '/templates',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['templates.read'],
          featureFlags: ['red_DV-3138_template_management'],
        },
      ],
    },
    {
      theme: 'red',
      title: 'Reports',
      icon: 'chart-pie-regular',
      permissions: [
        'reports.read',
        'purchases.read',
        'salesImports.read',
        'salesReport.read',
        'salesMargin.read',
      ],
      oldPermissions: [
        UserRoleConstants.SUPER_ADMIN,
        UserRoleConstants.ADMIN,
        UserRoleConstants.FACTORY_ADMIN,
        UserRoleConstants.SALES_MEMBER,
      ],
      parent: true,
      submenu: [
        {
          title: 'Reports',
          page: '/reports/list',
          oldPermissions: '*',
          permissions: ['reports.read'],
        },
        {
          title: 'Purchases',
          page: '/reports/purchases',
          adminOnly: true,
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['purchases.read'],
          isHiddenByDefault: true,
        },
        {
          title: 'Sales Imports',
          page: '/reports/sales-imports',
          adminOnly: true,
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['salesImports.read'],
          isHiddenByDefault: true,
        },
        {
          title: 'Sales Report',
          page: `/reports/sales-report`,
          adminOnly: true,
          oldPermissions: '*',
          permissions: ['salesReport.read'],
          isHiddenByDefault: true,
        },
        {
          title: 'Sales Margin',
          page: '/reports/sales-margin',
          adminOnly: true,
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['salesMargin.read'],
          isHiddenByDefault: true,
        },

        {
          title: 'Lot Tracking',
          page: 'reports/lot-tracking',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['reports.read'],
          featureFlags: [EFeatureFlag.LOT_TRACKING],
        },
        {
          title: 'Accounting',
          page: 'reports/accounting',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['reports.read'],
        },
        {
          title: 'Stock Snapshot',
          page: 'reports/stock-snapshot',
          oldPermissions: [],
          permissions: [''],
        },
        {
          title: 'Transaction COGS',
          page: 'reports/transaction-cogs',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['transactionCogs.read'],
        },
        {
          title: 'Consumption',
          page: 'reports/consumption-export',
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['reports.read'],
        },
      ],
    },

    {
      theme: 'cloud',
      title: 'Tools',
      icon: 'screwdriver-wrench-regular',
      oldPermissions: [
        UserRoleConstants.SUPER_ADMIN,
        UserRoleConstants.ADMIN,
        UserRoleConstants.FACTORY_ADMIN,
        UserRoleConstants.SALES_MEMBER,
      ],
      permissions: [
        'projectManagement.read',
        'inventoryRollback.read',
        'inventoryRollbackList.read',
        'marginCalculator.read',
        'inventoryPlanning.read',
        'imports.read',
      ],
      parent: true,
      submenu: [
        {
          title: 'Automation',
          page: '/automation-rules',
          oldPermissions: '*',
          permissions: [''],
          isHiddenByDefault: true,
        },
        {
          title: 'Project Management',
          page: '/inventory/new-designs',
          oldPermissions: '*',
          permissions: ['projectManagement.read'],
          isHiddenByDefault: true,
        },
        {
          title: 'Margin Calculator',
          page: '/tools/margin-calculator',
          adminOnly: true,
          oldPermissions: '*',
          permissions: ['marginCalculator.read'],
          isHiddenByDefault: true,
        },
        {
          title: 'Inventory Planning',
          page: '/tools/inventory-planning',
          adminOnly: true,
          oldPermissions: [
            UserRoleConstants.SUPER_ADMIN,
            UserRoleConstants.ADMIN,
            UserRoleConstants.FACTORY_ADMIN,
          ],
          permissions: ['inventoryPlanning.read'],
          isHiddenByDefault: true,
        },
        {
          title: 'Imports',
          page: '/tools/imports',
          adminOnly: true,
          oldPermissions: '*',
          permissions: ['imports.read'],
        },
        {
          title: 'Warehouse Mobile App',
          page: '/warehouse-mobile-app',
          oldPermissions: '*',
          permissions: ['locations.read'],
        },
      ],
    },
    {
      theme: 'gray',
      title: 'Custom Module',
      icon: 'external-link-regular',
      page: '/embed-replit',
      permissions: [],
      oldPermissions: '*',
    },
  ];
}

export function menuConfig(fts :Record<string, any>) {
  return {
    header: {
      self: {},
      items: [],
    },
    aside: {
      self: {},
      items: filterMenuItems(menuItems(fts)),
    },
  };
}

function filterMenuItems(items) {
  return items
    .map((item) => {
      if (item?.submenu) {
        item.submenu = item?.submenu.filter((a) => a);
      }

      return item;
    })
    .filter((a) => a);
}
